package com.dotdrives.web.pages.donations

import com.dotdrives.controller.DonationDetailController
import com.dotdrives.controller.DonationDetailUiState
import com.dotdrives.web.ui.DotBanner
import com.dotdrives.web.ui.DotSkeleton
import react.FC
import react.Props
import react.dom.aria.AriaRole
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.span
import web.cssom.ClassName
import web.html.ButtonType
import web.html.button as buttonType

external interface DonationDetailPanelProps : Props {
    var state: DonationDetailUiState
    var controller: DonationDetailController
    var canWrite: Boolean
    var onClose: () -> Unit
}

val DonationDetailPanel = FC<DonationDetailPanelProps> { props ->
    val donation = props.state.donation

    div {
        className = ClassName("donations-detail-backdrop")
        role = "presentation".unsafeCast<AriaRole>()
        onClick = { props.onClose() }

        div {
            className = ClassName("donations-detail-panel")
            role = "dialog".unsafeCast<AriaRole>()
            ariaModal = true
            ariaLabel = "Donation details"
            onClick = { it.stopPropagation() }

            if (props.state.loading && donation == null) {
                div {
                    className = ClassName("donations-detail-loading")
                    repeat(6) { DotSkeleton { height = if (it == 0) "54px" else "36px" } }
                }
            } else if (donation == null) {
                div {
                    className = ClassName("donations-detail-loading")
                    props.state.error?.let { error ->
                        DotBanner { message = error; onRetry = props.controller::retry }
                    }
                }
            } else {
                val status = qboStatus(donation)
                div {
                    className = ClassName("donations-detail-head")
                    div {
                        className = ClassName("donations-detail-head-row")
                        div {
                            div { className = ClassName("donations-detail-id"); +"DONATION #${donation.id.take(8).uppercase()}" }
                            div {
                                className = ClassName(
                                    "donations-detail-amount" + if (donation.amount < 0) " donations-detail-amount-negative" else "",
                                )
                                +formatMoneyFull(donation.amount)
                            }
                            div {
                                className = ClassName("donations-detail-sub")
                                +listOfNotNull(donation.giverDisplayName, donation.target, dateChip(donation.occurredAt))
                                    .joinToString(" · ")
                            }
                        }
                        button {
                            type = ButtonType.buttonType
                            className = ClassName("donations-detail-close")
                            ariaLabel = "Close donation details"
                            onClick = { props.onClose() }
                            +"✕"
                        }
                    }
                }
                div {
                    className = ClassName("donations-detail-body")
                    div {
                        className = ClassName("donations-detail-rows")
                        detailRow("Giver", donation.giverDisplayName)
                        if (!donation.isActive) detailRow("Status", "Inactive · imported history (read-only)")
                        detailRow("Donation target", donation.target ?: "No target")
                        donation.driveTitle?.let { detailRow("Linked drive", it) }
                        detailRow("Source", "${sourceLabel(donation.source)} · recorded ${dateChip(donation.recordedAt)}")
                        detailRow("Payment type", donation.paymentType.replaceFirstChar(Char::uppercase))
                        detailRow(
                            "QuickBooks",
                            when (status) {
                                QboStatus.Synced -> "Synced ✓"
                                QboStatus.Error -> donation.qboSyncError ?: "Sync error"
                                else -> qboStatusLabel(status)
                            },
                        )
                        val receiptSentAt = donation.receiptSentAt
                        detailRow("Receipt", if (receiptSentAt != null) "Accepted by email provider ${dateChip(receiptSentAt)}" else "Not sent")
                        if (donation.memo.isNotBlank()) detailRow("Memo", "\"${donation.memo}\"")
                        if (donation.isReversal) detailRow("Reversal", "Corrects a prior gift")
                    }
                    if (props.canWrite && donation.isActive && !donation.isReversal && donation.amount > 0) {
                        button {
                            type = ButtonType.buttonType
                            className = ClassName("donations-detail-refund")
                            disabled = props.state.reversing
                            onClick = {
                                val confirmed = kotlinx.browser.window.asDynamic()
                                    .confirm("Refund ${formatMoneyFull(donation.amount)} from ${donation.giverDisplayName}? This records a reversal and cannot be undone.")
                                    .unsafeCast<Boolean>()
                                if (confirmed) props.controller.reverse()
                            }
                            +(if (props.state.reversing) "Refunding…" else "Refund…")
                        }
                    }
                }
            }
        }
    }
}

private fun react.ChildrenBuilder.detailRow(key: String, value: String) {
    div {
        className = ClassName("donations-detail-row")
        span { className = ClassName("donations-detail-row-key"); +key }
        span { className = ClassName("donations-detail-row-val"); +value }
    }
}
